import { StandardLoader } from 'base/Loader/Loader';
import { t } from 'i18next';
import React, { useCallback, useEffect, useRef, useMemo } from 'react';

interface InfinityScrollProps {
  children: React.ReactNode;
  loading: boolean;
  dataLength: number;
  childrenIdPrefix: string;
  setLoading: (value: boolean) => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  hasMore: boolean;
  limitPerPage: number;
  loadingComponent?: React.ReactNode;
  endMessage?: React.ReactNode;
  errorComponent?: React.ReactNode;
  threshold?: number;
  rootMargin?: string;
  onError?: (error: Error) => void;
  debounceMs?: number;
}

const InfinityScroll: React.FC<InfinityScrollProps> = ({
  children,
  childrenIdPrefix,
  currentPage,
  dataLength,
  hasMore,
  limitPerPage,
  loading,
  setCurrentPage,
  setLoading,
  threshold = 0.8, // Trigger when 80% visible instead of 100%
  rootMargin = '100px', // Load content 100px before reaching the element
  onError,
  debounceMs = 300
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetElementRef = useRef<HTMLElement | null>(null);
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);

  // Memoize observer options to prevent unnecessary re-creations
  const observerOptions = useMemo<IntersectionObserverInit>(
    () => ({
      root: null,
      rootMargin,
      threshold
    }),
    [rootMargin, threshold]
  );

  // Debounced load function to prevent rapid successive calls
  const debouncedLoad = useCallback(async () => {
    const now = Date.now();
    if (now - lastLoadTimeRef.current < debounceMs) {
      console.log('InfinityScroll: Debounced load');
      return;
    }
    lastLoadTimeRef.current = now;

    if (isLoadingRef.current || !hasMore) {
      console.log('InfinityScroll: Already loading or no more data');
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setCurrentPage(currentPage + 1);
    } catch (error) {
      console.error('InfinityScroll load error:', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [hasMore, currentPage, setLoading, setCurrentPage, onError, debounceMs]);

  // Observer callback with improved error handling
  const observerCallback = useCallback<IntersectionObserverCallback>(
    entries => {
      const entry = entries[0];
      if (entry?.isIntersecting && hasMore && !isLoadingRef.current) {
        debouncedLoad();
      }
    },
    [hasMore, debouncedLoad]
  );

  // Effect to handle intersection observer setup and cleanup
  useEffect(() => {
    // Don't set up observer if we're loading or have no more data
    if (loading || !hasMore) {
      return;
    }

    // Handle initial load case
    if (dataLength === 0 && currentPage === 1) {
      console.log('InfinityScroll: Initial load');
      return;
    }

    const fallbackId = `${childrenIdPrefix}-${currentPage * limitPerPage}`;
    const fallbackElement = document.getElementById(fallbackId);

    if (!fallbackElement) {
      console.warn(
        `InfinityScroll: Target element with ID or "${fallbackId}" not found`
      );
      return;
    }

    targetElementRef.current = fallbackElement;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      observerCallback,
      observerOptions
    );
    observerRef.current.observe(targetElementRef.current);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      targetElementRef.current = null;
    };
  }, [
    hasMore,
    loading,
    dataLength,
    currentPage,
    childrenIdPrefix,
    limitPerPage,
    observerCallback,
    observerOptions,
    debouncedLoad
  ]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="infinity-scroll-container">
      {children}

      {loading && <StandardLoader />}

      {!hasMore && !loading && dataLength > 0 && (
        <div className="infinity-scroll-end py-3 text-center border-1 border-top border-bottom fw-bolder fs-6">
          {t('general.text_6')}
        </div>
      )}
    </div>
  );
};

export default InfinityScroll;
